import CSBinReader from './CSBinReader'
import EventClassMap from './EventClassMap'
import ParseCSBin from './ParseCSBin'

type ParsedObject = Record<string, any>

export default class ClassParse {
  public clzId = -1
  public className = ''
  public pros: string[] = []
  public types: number[] = []
  public typeEx: any[] = []
  public proCount = 0

  public parse(reader: CSBinReader, isRoot = false): any {
    ParseCSBin.consumeStep(reader, 'enter parse')
    const ex = reader.readIntOne()
    if (ex === 0x05 || ex === 0x0c) {
      let retIsNeed = false
      let isReplacementChar = false
      while (true) {
        ParseCSBin.consumeStep(reader, 'scan replacement char')
        const beforeNext = reader.start
        const next = Array.from(reader.readSome(3))
          .map((item) => item.toString(16).padStart(2, '0'))
          .join('')
        ParseCSBin.ensureProgress(reader, beforeNext, 'scan replacement char')
        const replacement = 'efbfbd'
        if (next !== replacement) {
          reader.start -= 3
          break
        }
        isReplacementChar = true
      }

      if (!isReplacementChar) {
        this.clzId = reader.readInt()
      }

      let obj: ParsedObject | undefined = this.clzId !== 0x01 ? ParseCSBin.objDict.get(this.clzId) : undefined
      if (obj !== undefined) {
        retIsNeed = true
      } else {
        obj = { __id: this.clzId }
        ParseCSBin.objDict.set(this.clzId, obj)
      }

      if (ex === 0x0c) {
        reader.readCSBinString()
        if (isRoot) {
          return new ClassParse().parse(reader)
        }
        if (!retIsNeed) {
          return new ClassParse().parse(reader)
        }
        new ClassParse().parse(reader)
        return obj
      }

      this.className = reader.readCSBinString()
      this.proCount = reader.readInt()
      this.setProperly(obj, reader)
      reader.readStringLen(4)
      ParseCSBin.clzDict.set(this.clzId, this)
      this.setValue(obj, reader)

      const eventClassID = EventClassMap.getInstance().getEventClassID(this.className)
      if (eventClassID !== -1) {
        obj.Code = eventClassID
        EventClassMap.getInstance().removeEventBaseP(obj)
      }
      return obj
    }

    if (ex === 0x01) {
      const objId = reader.readInt()
      let obj = ParseCSBin.objDict.get(objId)
      this.clzId = reader.readInt()
      const clz = ParseCSBin.clzDict.get(this.clzId)
      if (clz === undefined) {
        throw new Error(`Unknown CSBin class: obj=${objId}, class=${this.clzId}`)
      }

      this.proCount = clz.proCount
      this.className = clz.className
      this.pros = clz.pros
      this.types = clz.types
      this.typeEx = clz.typeEx
      this.clzId = clz.clzId

      if (obj !== undefined) {
        this.setValue(obj, reader)
        const eventClassID = EventClassMap.getInstance().getEventClassID(this.className)
        if (eventClassID !== -1) {
          obj.Code = eventClassID
          EventClassMap.getInstance().removeEventBaseP(obj)
        }
        return obj
      }

      if (objId < 0) {
        obj = { __id: objId }
        this.setValue(obj, reader, false)
        return obj
      }

      return undefined
    }

    if (ex === 0x09) {
      const objId = reader.readInt()
      let obj = ParseCSBin.objDict.get(objId)
      if (obj === undefined) {
        obj = { __id: String(objId) }
      }
      ParseCSBin.objDict.set(objId, obj)
      return obj
    }

    if (ex === 0x04) {
      this.clzId = reader.readInt()
      let obj: ParsedObject | undefined = this.clzId !== 0x01 ? ParseCSBin.objDict.get(this.clzId) : { __id: 1 }
      if (obj === undefined) {
        obj = { __id: this.clzId }
      }
      this.className = reader.readCSBinString()
      this.proCount = reader.readInt()
      this.setProperly(obj, reader)
      ParseCSBin.clzDict.set(this.clzId, this)
      try {
        this.setValue(obj, reader, obj.__id >= 0)
      } catch {
        /* ignore malformed tail and return best-effort object */
      }
      return obj
    }

    if (ex === 0x07) {
      this.clzId = reader.readInt()
      const obj: ParsedObject = this.clzId !== 0x01 ? ParseCSBin.objDict.get(this.clzId) ?? {} : {}
      obj.items = []
      reader.readStringLen(5)
      let allLen = reader.readInt()
      const innerEx = reader.readIntOne()
      if (innerEx !== 0x06) {
        this.className = reader.readCSBinString()
        if (innerEx !== 0x03) {
          reader.readInt()
        }
      }

      for (let i = 0; i < allLen; i++) {
        ParseCSBin.consumeStep(reader, 'read object list item')
        const itemEx = reader.readIntOne()
        if (itemEx === 0x0a) {
          allLen -= 1
          break
        }
        if (itemEx === 0x0d) {
          const len = reader.readIntOne()
          allLen -= len
          break
        }
        if (itemEx === 0x0e) {
          const len = reader.readInt16LEValue()
          allLen -= len
          break
        }
        reader.start -= 1
        obj.items.push(new ClassParse().parse(reader))
      }

      for (let i = 0; i < allLen; i++) {
        ParseCSBin.consumeStep(reader, 'read object list tail')
        if (obj.items[i] !== 'null') {
          new ClassParse().parse(reader)
        }
      }
      return undefined
    }

    if (ex === 0x10 || ex === 0x11) {
      this.clzId = reader.readInt()
      const obj: ParsedObject = this.clzId !== 0x01 ? ParseCSBin.objDict.get(this.clzId) ?? {} : {}
      let allLen = reader.readInt()
      const values: any[] = []
      for (let i = 0; i < allLen; i++) {
        ParseCSBin.consumeStep(reader, 'read value list item')
        const itemEx = reader.readIntOne()
        if (itemEx === 0x08) {
          reader.readIntOne()
          values.push(reader.readInt())
        } else if (itemEx === 0x0d) {
          const len = reader.readIntOne()
          allLen -= len
        } else if (itemEx === 0x0e) {
          const len = reader.readInt()
          allLen -= len
        } else if (itemEx === 0x09) {
          reader.start -= 1
          values.push(new ClassParse().parse(reader))
        } else {
          reader.start -= 1
          values.push(reader.readCSBinValueString())
        }
      }

      obj.items = values
      for (let i = 0; i < obj.items.length; i++) {
        if (obj.items[i]?.__id !== undefined) {
          new ClassParse().parse(reader)
        }
      }
      return obj
    }

    if (ex === 0x0f) {
      this.clzId = reader.readInt()
      const obj: ParsedObject = this.clzId !== 0x01 ? ParseCSBin.objDict.get(this.clzId) ?? {} : {}
      const allLen = reader.readInt()
      const values: number[] = []
      const type = reader.readIntOne()
      for (let i = 0; i < allLen; i++) {
        ParseCSBin.consumeStep(reader, 'read primitive list item')
        if (type === 0x08) {
          values.push(reader.readInt())
        } else if (type === 0x01 || type === 0x02) {
          values.push(reader.readIntOne())
        }
      }
      obj.items = values
      return obj
    }

    if (ex === 0x0b) {
      return true
    }
    if (ex === 0x0a) {
      return 'null'
    }
    if (ex === 0xd4) {
      this.className = reader.readCSBinValueString()
      return undefined
    }
    if (ex === 0xff || ex === 0x00) {
      return 'null'
    }

    return undefined
  }

  public setProperly(obj: ParsedObject, reader: CSBinReader): void {
    for (let i = 0; i < this.proCount; i++) {
      ParseCSBin.consumeStep(reader, 'read property name')
      const prop = reader.readCSBinString()
      this.pros.push(prop)
    }

    for (let i = 0; i < this.proCount; i++) {
      ParseCSBin.consumeStep(reader, 'read property type')
      this.types.push(reader.readIntOne())
    }

    for (let i = 0; i < this.proCount; i++) {
      ParseCSBin.consumeStep(reader, 'read property type extra')
      if (this.types[i] === 0x04) {
        const ex = reader.readCSBinString()
        if (ex.includes('typeF')) {
          this.typeEx.push('enum')
        } else {
          this.typeEx.push('class')
        }
        reader.readStringLen(4)
        obj[this.pros[i]!] = {}
      } else if (this.types[i] === 0x01) {
        obj[this.pros[i]!] = ''
        this.typeEx.push('string')
      } else if (this.types[i] === 0x00) {
        const ex = reader.readIntOne()
        if (ex === 0x01) {
          obj[this.pros[i]!] = false
        } else if (ex === 0x08 || ex === 0x0b || ex === 0x06) {
          obj[this.pros[i]!] = 0
        }
        this.typeEx.push(ex)
      } else if (this.types[i] === 0x03) {
        obj[this.pros[i]!] = []
        const ex = reader.readCSBinString()
        if (ex.includes('IComparer') || ex.includes('IHashCodeProvider')) {
          this.typeEx.push(ex)
        } else {
          this.typeEx.push('list')
        }
      } else if (this.types[i] === 0x05) {
        this.typeEx.push('class')
      } else if (this.types[i] === 0x06) {
        this.typeEx.push('list')
      } else if (this.types[i] === 0x07) {
        reader.readIntOne()
        this.typeEx.push('list')
      }
    }
  }

  public setValue(obj: ParsedObject, reader: CSBinReader, readData = true): void {
    for (let i = 0; i < this.proCount; i++) {
      ParseCSBin.consumeStep(reader, 'read property value')
      const prop = this.pros[i]!
      const typeEx = this.typeEx[i]
      if (typeEx === 'string') {
        obj[prop] = reader.readCSBinValueString()
      } else if (typeEx === 0x08 || typeEx === 0x0f) {
        obj[prop] = reader.readInt()
      } else if (typeEx === 0x06) {
        obj[prop] = reader.readDouble()
      } else if (typeEx === 0x0b) {
        obj[prop] = reader.readFloat()
      } else if (typeEx === 0x01) {
        obj[prop] = reader.readIntOne() === 1
      } else if (typeEx === 'class' || typeEx === 'list' || typeEx === 'enum') {
        obj[prop] = new ClassParse().parse(reader)
      } else if (typeEx === 0x10) {
        obj[prop] = reader.readULong64()
      } else if (typeof typeEx === 'string' && (typeEx.includes('IComparer') || typeEx.includes('IHashCodeProvider'))) {
        obj[prop] = new ClassParse().parse(reader)
      } else {
        throw new Error(`Unknown CSBin value type: ${String(typeEx)}`)
      }
    }

    if (!readData) {
      return
    }

    for (let i = 0; i < this.proCount; i++) {
      ParseCSBin.consumeStep(reader, 'read deferred property payload')
      const prop = this.pros[i]!
      const typeEx = this.typeEx[i]
      if ((typeEx === 'class' || typeEx === 'list') && obj[prop] !== 'null') {
        new ClassParse().parse(reader)
      }
    }
  }
}
